import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { refresh } from './auth-api';
import { setSession } from './auth-store';

interface Props {
  children: ReactNode;
}

/**
 * Attempts a silent refresh on app load. The refresh cookie (httpOnly) is set
 * on a successful login/register; if it's still valid, the server hands back a
 * fresh access token and we materialise the in-memory session.
 */
export function AuthBootstrap({ children }: Props): ReactElement {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await refresh();
        if (cancelled) return;
        setSession({
          user: body.user,
          activeOrg: body.activeOrg,
          accessToken: body.accessToken,
          accessTokenExpiresAt: body.accessTokenExpiresAt,
        });
      } catch {
        // ignore: user is simply not logged in
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        {t('auth.bootstrap.loading')}
      </div>
    );
  }
  return <>{children}</>;
}
